import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Chip } from '@mui/material';
import { SmartToyOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import AssistantChat from '../components/AssistantChat';

const AssistantPage: React.FC = () => {
  const { user } = useAuth();
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';
  const navigate = useNavigate();
  const [status, setStatus] = useState<{ enabled: boolean; model: string | null } | null>(null);

  const hasAssistantAccess = !!user?.assistant_enabled;

  useEffect(() => {
    if (!hasAssistantAccess) { navigate('/access-denied'); return; }
    apiService.getAssistantStatus().then(setStatus).catch(() => setStatus({ enabled: false, model: null }));
  }, [hasAssistantAccess, navigate]);

  const textP = isDark ? 'rgba(255,255,255,0.92)' : 'rgba(0,0,0,0.9)';
  const textM = isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.55)';
  const border = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const surface = isDark ? '#16162a' : '#ffffff';

  if (!hasAssistantAccess) return null;

  return (
    <Box sx={{ p: 3, height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <SmartToyOutlined sx={{ color: '#3B82F6', fontSize: 26 }} />
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: textP }}>Platform Assistant</Typography>
          <Typography sx={{ fontSize: '0.78rem', color: textM }}>
            Read-only help for admins. Ask about apps, permissions, request flows, and common errors.
          </Typography>
        </Box>
        {status?.model && (
          <Chip
            size="small"
            label={status.model}
            sx={{ fontFamily: '"JetBrains Mono","Courier New",monospace', fontSize: '0.7rem', bgcolor: isDark ? 'rgba(59,130,246,0.15)' : '#dbeafe', color: '#3B82F6', border: 'none' }}
          />
        )}
      </Box>

      {status && !status.enabled ? (
        <Alert severity="warning">
          The assistant is disabled. Ask a super admin to set <code>ASSISTANT_ENABLED=true</code> and
          configure <code>ASSISTANT_OPENAI_API_KEY</code>, then restart the admin service.
        </Alert>
      ) : (
        <Box sx={{ flex: 1, border: `1px solid ${border}`, borderRadius: 2, overflow: 'hidden', bgcolor: surface }}>
          <AssistantChat fullHeight />
        </Box>
      )}
    </Box>
  );
};

export default AssistantPage;
